import { useState, useEffect } from 'react';
import { Button, Input, Table, Select } from '../../components/ui';
import { api } from '../../services/api-client';

interface UserRow {
  profileId: string;
  firstName: string;
  lastName: string;
  codiceFiscale: string;
  personalEmail: string;
  status: string;
  censedAt: string;
}

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'CENSED', label: 'Censito' },
  { value: 'INVITED', label: 'Invitato' },
  { value: 'ACTIVE', label: 'Attivo' },
  { value: 'SUSPENDED', label: 'Sospeso' },
  { value: 'DEACTIVATED', label: 'Disattivato' },
];

const STATUS_LABELS: Record<string, string> = {
  CENSED: '🟡 Censito',
  INVITED: '📧 Invitato',
  REGISTERED: '✅ Registrato',
  ACTIVE: '🟢 Attivo',
  SUSPENDED: '🔴 Sospeso',
  DEACTIVATED: '⚫ Disattivato',
};

/**
 * Admin page: User list with search and filters.
 */
const PAGE_SIZE = 50;

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const loadUsers = async (targetPage: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(targetPage));
      params.set('pageSize', String(PAGE_SIZE));
      const response = await api.get<{
        data: UserRow[];
        pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
      }>(`/admin/users?${params.toString()}`);
      setUsers(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
      setPage(response.data.pagination.page);
    } catch {
      setUsers([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(1); }, []);

  // New search/filter always restarts from page 1
  const handleSearch = () => { loadUsers(1); };

  const goToPage = (target: number) => {
    if (target < 1 || target > totalPages || target === page) return;
    loadUsers(target);
  };

  const columns = [
    { key: 'lastName', header: 'Cognome' },
    { key: 'firstName', header: 'Nome' },
    { key: 'codiceFiscale', header: 'Codice Fiscale' },
    { key: 'personalEmail', header: 'Email' },
    {
      key: 'status',
      header: 'Stato',
      render: (item: UserRow) => STATUS_LABELS[item.status] || item.status,
    },
    {
      key: 'censedAt',
      header: 'Data censimento',
      render: (item: UserRow) => item.censedAt ? new Date(item.censedAt).toLocaleDateString('it-IT') : '-',
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (item: UserRow) => (
        <a href={`/admin/users/${item.profileId}`} className="btn btn-ghost btn-sm">
          Dettaglio
        </a>
      ),
    },
  ];

  // Build a compact list of page numbers with ellipsis: 1 … p-1 p p+1 … N
  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <div className="admin-users-page admin-users-page--wide">
      <div className="page-header">
        <h2>Gestione Utenti</h2>
        <a href="/admin/census" className="btn btn-primary">
          + Nuovo Utente
        </a>
      </div>

      <div className="filters-bar">
        <Input
          label="Cerca"
          placeholder="Nome, cognome, CF o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Select
          label="Stato"
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); }}
        />
        <div className="filters-search-action">
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            Cerca
          </Button>
        </div>
      </div>

      <Table<UserRow>
        columns={columns}
        data={users}
        keyField="profileId"
        loading={loading}
        emptyMessage="Nessun utente trovato. Usa il pulsante '+ Nuovo Utente' per censire il primo utente."
      />

      {totalPages > 1 && (
        <nav className="pagination" role="navigation" aria-label="Paginazione utenti">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => goToPage(page - 1)}
            disabled={page === 1 || loading}
            aria-label="Pagina precedente"
          >
            ← Precedente
          </button>

          <div className="pagination-pages">
            {pageNumbers.map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
              ) : (
                <button
                  type="button"
                  key={p}
                  className={`pagination-page${p === page ? ' pagination-page--active' : ''}`}
                  onClick={() => goToPage(p as number)}
                  disabled={loading}
                  aria-current={p === page ? 'page' : undefined}
                >
                  {p}
                </button>
              ),
            )}
          </div>

          <button
            type="button"
            className="pagination-btn"
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages || loading}
            aria-label="Pagina successiva"
          >
            Successiva →
          </button>
        </nav>
      )}

      {totalItems > 0 && (
        <p className="pagination-summary">
          {totalItems.toLocaleString('it-IT')} utenti · pagina {page} di {totalPages}
        </p>
      )}
    </div>
  );
}

/**
 * Returns a compact list of page numbers with ellipsis markers.
 * Always shows first, last, current and its neighbours.
 * Example (current 6 of 20): [1, '...', 5, 6, 7, '...', 20]
 */
function getPageNumbers(current: number, total: number): Array<number | '...'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: Array<number | '...'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');

  pages.push(total);
  return pages;
}
